import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionMasters } from "../../store/slices/sessionSlice";
import { fetchSubSessionMasters } from "../../store/slices/subSessionSlice";
import { fetchPatients } from "../../store/slices/patientsSlice";
import { fetchSessionCategories } from "../../store/slices/sessionCategorySlice";
import Select from "react-select";

const UserSessionForm = ({ formData, handleChange }) => {
    const dispatch = useDispatch();
    const { sessionMasters } = useSelector((state) => state.sessions);
    const { subSessionMasters } = useSelector((state) => state.subSessions);
    const { patients } = useSelector((state) => state.patients);
    const { sessionCategories } = useSelector((state) => state.sessionCategories);

    useEffect(() => {
        dispatch(fetchSessionMasters());
        dispatch(fetchSubSessionMasters());
        dispatch(fetchPatients());
        dispatch(fetchSessionCategories());
    }, [dispatch]);

    // Options mapping
    const activeCategories = (sessionCategories || []).filter(c => c.status === "active");
    const categoryOptions = activeCategories.map(c => ({
        value: c.id,
        label: c.name
    }));

    const activeSessions = (sessionMasters || []).filter(
        s => s.status === "active" && (!formData.sessionCategoryMasterId || s.session_category_master_id === parseInt(formData.sessionCategoryMasterId))
    );
    const sessionOptions = activeSessions.map(s => ({
        value: s.id,
        label: s.name
    }));

    // Dynamic Sub Sessions filtering
    const activeSubSessions = (subSessionMasters || []).filter(
        sub => sub.status === "active" && sub.session_master_id === parseInt(formData.sessionMasterId)
    );
    const subSessionOptions = activeSubSessions.map(sub => ({
        value: sub.id,
        label: sub.name
    }));

    // Patients options mapping
    const patientOptions = (patients || []).map(p => ({
        value: p.id,
        label: `${p.first_name || ""} ${p.last_name || ""} (${p.mr_no || "N/A"})`.trim()
    }));

    // Satisfaction dropdown options
    const satisfactionOptions = [
        { value: "Very good", label: "Very good" },
        { value: "Good", label: "Good" },
        { value: "Average", label: "Average" },
        { value: "Bad", label: "Bad" },
        { value: "Very bad", label: "Very bad" }
    ];

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '38px',
            fontSize: '0.875rem',
            borderRadius: '0.375rem',
            borderColor: '#d9dee3',
            '&:hover': { borderColor: '#d9dee3' }
        }),
        menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#666ee8' : state.isFocused ? '#f5f5f9' : 'white',
            color: state.isSelected ? 'white' : '#566a7f',
            cursor: 'pointer',
        })
    };

    const currentCategory = categoryOptions.find(opt => opt.value === parseInt(formData.sessionCategoryMasterId)) || null;
    const currentSession = sessionOptions.find(opt => opt.value === parseInt(formData.sessionMasterId)) || null;
    const currentSubSession = subSessionOptions.find(opt => opt.value === parseInt(formData.subSessionMasterId)) || null;
    const currentPatient = patientOptions.find(opt => opt.value === parseInt(formData.patientId)) || null;
    const currentSatisfaction = satisfactionOptions.find(opt => opt.value === formData.satisfaction) || null;

    return (
        <div className="content p-4">
            <div className="row g-3">
                <div className="col-md-12">
                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-2 py-1 mb-3">
                        Session Setup
                    </small>
                </div>

                {/* Patient selection */}
                <div className="col-md-3">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Patient <span className="text-danger">*</span></label>
                        <Select
                            options={patientOptions}
                            styles={selectStyles}
                            value={currentPatient}
                            onChange={(opt) => handleChange({ target: { name: "patientId", value: opt ? opt.value : "" } })}
                            placeholder="Search Patient"
                            isSearchable
                            isDisabled={!!formData.patientIdReadOnly}
                        />
                    </div>
                </div>

                {/* Category selection */}
                <div className="col-md-3">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Session Category <span className="text-danger">*</span></label>
                        <Select
                            options={categoryOptions}
                            styles={selectStyles}
                            value={currentCategory}
                            onChange={(opt) => {
                                handleChange({ target: { name: "sessionCategoryMasterId", value: opt ? opt.value : "" } });
                                // Reset session and sub-session selection when category changes
                                handleChange({ target: { name: "sessionMasterId", value: "" } });
                                handleChange({ target: { name: "subSessionMasterId", value: "" } });
                            }}
                            placeholder="Select Category"
                            isSearchable
                        />
                    </div>
                </div>

                {/* Session Type selection */}
                <div className="col-md-3">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Session Type <span className="text-danger">*</span></label>
                        <Select
                            options={sessionOptions}
                            styles={selectStyles}
                            value={currentSession}
                            onChange={(opt) => {
                                handleChange({ target: { name: "sessionMasterId", value: opt ? opt.value : "" } });
                                // Reset sub-session selection when parent changes
                                handleChange({ target: { name: "subSessionMasterId", value: "" } });
                            }}
                            placeholder={formData.sessionCategoryMasterId ? "Select Session Type" : "Select Category First"}
                            isSearchable
                            isDisabled={!formData.sessionCategoryMasterId}
                        />
                    </div>
                </div>

                {/* Sub Session Type selection */}
                <div className="col-md-3">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Sub Session Type <span className="text-danger">*</span></label>
                        <Select
                            options={subSessionOptions}
                            styles={selectStyles}
                            value={currentSubSession}
                            onChange={(opt) => handleChange({ target: { name: "subSessionMasterId", value: opt ? opt.value : "" } })}
                            placeholder={formData.sessionMasterId ? "Select Sub Session" : "Select Session Type First"}
                            isSearchable
                            isDisabled={!formData.sessionMasterId}
                        />
                    </div>
                </div>

                <div className="col-md-12">
                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-2 py-1 mb-3 mt-3">
                        Session Details
                    </small>
                </div>

                {/* Date Selection */}
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="date"
                            className="form-control"
                            name="date"
                            value={formData.date || ""}
                            onChange={handleChange}
                            required
                        />
                        <label>Date <span className="text-danger">*</span></label>
                    </div>
                </div>

                {/* Time Selection */}
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline mb-3">
                        <input
                            type="time"
                            className="form-control"
                            name="time"
                            value={formData.time || ""}
                            onChange={handleChange}
                            required
                        />
                        <label>Time <span className="text-danger">*</span></label>
                    </div>
                </div>

                {/* Satisfaction */}
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Satisfaction <span className="text-danger">*</span></label>
                        <Select
                            options={satisfactionOptions}
                            styles={selectStyles}
                            value={currentSatisfaction}
                            onChange={(opt) => handleChange({ target: { name: "satisfaction", value: opt ? opt.value : "" } })}
                            placeholder="Rate Satisfaction"
                            isSearchable={false}
                        />
                    </div>
                </div>

                {/* Summary of Session */}
                <div className="col-md-12">
                    <div className="form-floating form-floating-outline mb-3">
                        <textarea
                            className="form-control"
                            name="summaryOfSession"
                            value={formData.summaryOfSession || ""}
                            onChange={handleChange}
                            placeholder="Enter Session Summary"
                            style={{ height: "120px" }}
                        />
                        <label>Summary of Session</label>
                    </div>
                </div>

                {/* Satisfaction Feedback */}
                <div className="col-md-12">
                    <div className="form-floating form-floating-outline mb-3">
                        <textarea
                            className="form-control"
                            name="satisfactionFeedback"
                            value={formData.satisfactionFeedback || ""}
                            onChange={handleChange}
                            placeholder="Enter Satisfaction Feedback"
                            style={{ height: "100px" }}
                        />
                        <label>Satisfaction Feedback</label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserSessionForm;
